import styled from '@emotion/styled';
import {
    Button,
    ButtonGroup,
    Form,
    FormInputTextarea,
    pxToRem,
    useForm,
} from '@modernhealth/vital-components';
import { captureException } from 'src/shared/helpers/captureException';

import React, { useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { useTranslation } from 'react-i18next';

import { appClient } from 'src/config/apollo';
import { color, font } from 'src/shared/styles/variables';
import { NumbersRowInput } from 'src/shared/components/NumbersRowInput';

import { useSubmitNpsMutation } from '../documents/submitNps.generated';

const HeaderText = styled.h4(
    {
        color: color.ebonyClay,
        marginTop: 15,
        marginBottom: 40,
        textAlign: 'center',
    },
    font.black(font.size.extraLarge),
);

const RatingWrapper = styled.div({
    position: 'relative',
});

const SkipButtonWrapper = styled.div({
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    bottom: -140,
});

const ButtonGroupWrapper = styled.div({
    marginTop: pxToRem(35),
});

interface Props {
    onSkip: () => void;
    onSubmit: () => void;
    sessionUuid: string;
}

interface FormData {
    feedbackText: string;
}

export const RateSessionNPSQuestionStep: React.FC<Props> = ({ onSkip, onSubmit, sessionUuid }) => {
    const { t } = useTranslation();

    const [rating, setRating] = useState<null | number>(null);

    const [submitNps, { loading }] = useSubmitNpsMutation({
        client: appClient,
    });

    const formDefinition = useForm<FormData>();

    const handleSubmit = async (formData: FormData) => {
        if (rating === null || loading) {
            return;
        }

        try {
            const { data } = await submitNps({
                variables: {
                    coachingVisitUuid: sessionUuid,
                    rating,
                    feedbackText: formData.feedbackText,
                },
            });

            const ok = data?.submitNps?.ok;

            if (!ok) {
                throw new Error('SubmitNPSMutation: received non-ok response.');
            }
        } catch (error) {
            captureException(error);
        }

        onSubmit();
    };

    return (
        <React.Fragment>
            <HeaderText id="nps-question-label">
                {t('videoSessions:rate_session.nps_question.title')}
            </HeaderText>
            <RatingWrapper>
                <NumbersRowInput
                    value={rating}
                    onValueChange={v => setRating(v)}
                    range={[0, 10]}
                    minLabel={t('videoSessions:rate_session.nps_question.range_label.min')}
                    maxLabel={t('videoSessions:rate_session.nps_question.range_label.max')}
                />
                {rating === null ? (
                    <SkipButtonWrapper>
                        <Button onClick={onSkip} variant="flat" fluid>
                            {t('common.skip')}
                        </Button>
                    </SkipButtonWrapper>
                ) : null}
            </RatingWrapper>
            <AnimateHeight
                duration={300}
                height={rating === null ? 0 : 'auto'}
                style={{
                    width: '100%',
                    opacity: rating === null ? 0 : 1,
                    transition: 'opacity 0.3s ease-in-out 0.25s',
                }}
            >
                <Form definition={formDefinition} onSubmit={handleSubmit} css={{ gap: 0 }}>
                    <FormInputTextarea
                        rows={10}
                        placeholder={t(
                            'videoSessions:rate_session.nps_question.text_area.placeholder',
                        )}
                        name="feedbackText"
                        labelledBy="nps-question-label"
                    />

                    <ButtonGroupWrapper>
                        <ButtonGroup>
                            <Button onClick={onSkip} variant="secondary">
                                {t('common.skip')}
                            </Button>
                            <Button type="submit" busy={loading} variant="primary">
                                {t('common.submit')}
                            </Button>
                        </ButtonGroup>
                    </ButtonGroupWrapper>
                </Form>
            </AnimateHeight>
        </React.Fragment>
    );
};
